import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, BellOff, BellRing, Smartphone, Loader2, CalendarCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { API_URL, formatCurrency } from "../../lib/utils";
import { pushSupported, getCurrentSubscription, subscribePush, unsubscribePush } from "../../lib/push";

const KINDS = [
  { id: "cash_close", label: "Fecho de caixa", hint: "Quem fechou, contado vs. esperado, diferença e TPA" },
  { id: "low_stock", label: "Stock baixo / esgotado", hint: "Quando uma venda faz um artigo cruzar o mínimo" },
  { id: "security", label: "Alertas de segurança", hint: "Logins falhados repetidos, bloqueios e acessos de IP novo" },
  { id: "daily_summary", label: "Resumo diário (23h)", hint: "Total de vendas do dia, por modalidade e por TPA" },
  { id: "saft_reminder", label: "Lembrete SAF-T (dia 1, 08h)", hint: "Validar e exportar o SAF-T (AO) do mês anterior para entregar à AGT" },
];

// Notificações push no telemóvel/desktop do gerente (requer app instalada no iPhone)
export const PushSettings = ({ isManager }) => {
  const [sub, setSub] = useState(null);
  const [devices, setDevices] = useState([]);
  const [prefs, setPrefs] = useState({ cash_close: true, low_stock: true, security: true, daily_summary: true });
  const [busy, setBusy] = useState(false);
  const [sendingSummary, setSendingSummary] = useState(false);
  const supported = pushSupported();
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

  const load = async () => {
    try {
      const current = await getCurrentSubscription();
      setSub(current);
      const r = await axios.get(`${API_URL}/push/subscriptions`, { withCredentials: true });
      setDevices(r.data);
      const mine = current && r.data.find((d) => current.endpoint.endsWith(d.endpoint_tail));
      if (mine) setPrefs({ ...prefs, ...mine.prefs });
    } catch { /* sem subscrição */ }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const enable = async () => {
    setBusy(true);
    try {
      await subscribePush(prefs);
      toast.success("Notificações ativadas neste dispositivo");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Não foi possível ativar"); } finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try { await unsubscribePush(); toast.success("Notificações desativadas neste dispositivo"); await load(); } catch { toast.error("Erro ao desativar"); } finally { setBusy(false); }
  };
  const togglePref = async (id, v) => {
    const next = { ...prefs, [id]: v };
    setPrefs(next);
    if (sub) await axios.put(`${API_URL}/push/prefs`, { endpoint: sub.endpoint, prefs: next }, { withCredentials: true }).catch(() => toast.error("Erro ao guardar preferências"));
  };
  const test = async () => {
    try {
      const r = await axios.post(`${API_URL}/push/test`, {}, { withCredentials: true });
      if (r.data.sent < r.data.devices) toast.warning(`Enviada para ${r.data.sent} de ${r.data.devices} dispositivo(s) — algum dispositivo pode ter a subscrição expirada`);
      else toast.success(`Notificação de teste enviada para ${r.data.sent} dispositivo(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao enviar teste"); }
  };
  const sendSummary = async () => {
    setSendingSummary(true);
    try {
      const r = await axios.post(`${API_URL}/push/daily-summary/send`, {}, { withCredentials: true });
      const s = r.data.summary;
      if (r.data.sent > 0) toast.success(`Resumo de hoje enviado a ${r.data.sent} dispositivo(s): ${s.count} venda(s) · ${formatCurrency(s.total)}`, { duration: 7000 });
      else toast.warning(`Resumo gerado (${s.count} venda(s) · ${formatCurrency(s.total)}) mas nenhum gerente tem o "Resumo diário" ativo num dispositivo`, { duration: 7000 });
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao enviar resumo"); } finally { setSendingSummary(false); }
  };

  return (
    <Card data-testid="push-settings-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base md:text-lg"><BellRing className="w-5 h-5 text-cyan-500" /> Notificações no telemóvel</CardTitle>
        <CardDescription>Receba avisos de fecho de caixa, stock baixo, segurança e o resumo diário de vendas neste dispositivo, mesmo com a app fechada.{!isManager && " Apenas gerentes e administradores recebem estes avisos."}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!supported && <p className="text-sm text-amber-600" data-testid="push-unsupported">{ios && !standalone ? "No iPhone/iPad, instale primeiro o Lumora como aplicação (Partilhar → Adicionar ao ecrã principal) e abra-o a partir do ícone para ativar as notificações." : "Este navegador não suporta notificações push."}</p>}
        <div className="flex flex-wrap items-center gap-2">
          {sub ? (
            <Button variant="outline" onClick={disable} disabled={busy} className="gap-2" data-testid="push-disable-btn"><BellOff className="w-4 h-4" /> Desativar neste dispositivo</Button>
          ) : (
            <Button onClick={enable} disabled={busy || !supported} className="gap-2" data-testid="push-enable-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} Ativar notificações neste dispositivo</Button>
          )}
          {devices.length > 0 && <Button variant="ghost" onClick={test} className="gap-2" data-testid="push-test-btn"><Smartphone className="w-4 h-4" /> Enviar teste</Button>}
          {isManager && <Button variant="ghost" onClick={sendSummary} disabled={sendingSummary} className="gap-2" data-testid="push-daily-summary-btn">{sendingSummary ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarCheck className="w-4 h-4" />} Enviar resumo de hoje</Button>}
          <span className="text-xs text-muted-foreground" data-testid="push-device-count">{devices.length} dispositivo(s) ativo(s)</span>
        </div>
        {!sub && supported && <p className="text-xs text-muted-foreground" data-testid="push-prefs-hint">As preferências abaixo aplicam-se a este dispositivo e são guardadas quando ativar as notificações.</p>}
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {KINDS.map((k) => (
            <label key={k.id} className="flex items-start justify-between gap-3 rounded-lg border p-3 cursor-pointer" data-testid={`push-pref-${k.id}`}>
              <span><span className="text-sm font-medium block">{k.label}</span><span className="text-xs text-muted-foreground">{k.hint}</span></span>
              <Switch checked={prefs[k.id] !== false} onCheckedChange={(v) => togglePref(k.id, v)} data-testid={`push-pref-switch-${k.id}`} />
            </label>
          ))}
        </div>
        {devices.length > 0 && (
          <ul className="text-xs text-muted-foreground space-y-1" data-testid="push-devices-list">
            {devices.map((d) => <li key={d.sub_id}>• {d.device_label || "Dispositivo"} — desde {new Date(d.created_at).toLocaleDateString("pt-PT")}{d.last_sent_at ? ` · último aviso ${new Date(d.last_sent_at).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" })}` : ""}</li>)}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
